import {
  alpha,
  Box,
  Grid,
  Icon,
  Paper,
  SvgIconTypeMap,
  Typography,
  useTheme,
} from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { animated, useSpring } from "@react-spring/web";

type SmallOverViewWithIconProps = {
  height: number;
  titleMain: string;
  titleSecondary: string;
  contentMain: number;
  contentSecondary: number;
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  iconColor: string;
};

const AnimatedNumber = (n: number) => {
  const { number } = useSpring({
    from: { number: 0 },
    number: n,
    delay: 100,
    config: { mass: 1, tension: 20, friction: 10 },
  });
  return <animated.div>{number.to((n) => n.toFixed(0))}</animated.div>;
};

export default function SmallOverviewWithIcon({
  height,
  titleMain,
  titleSecondary,
  contentMain,
  contentSecondary,
  icon: Icon,
  iconColor,
}: SmallOverViewWithIconProps) {
  const theme = useTheme();
  return (
    <Paper sx={{ borderRadius: "5px", height: height }}>
      <Box
        sx={{ height: "100%", borderRadius: "5px" }}
        justifyContent={"center"}
        display={"flex"}
      >
        <Grid container flexDirection={"row"} alignItems={"center"} spacing={2}>
          <Grid
            item
            xs={4}
            height={"100%"}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Box
              sx={{
                fontSize: "5rem",
                borderRadius: "5px 0 0 5px",
                backgroundColor: alpha(iconColor, 0.1),
                width: "100%",
                height: "100%",
              }}
              alignContent={"center"}
              justifyContent={"center"}
              alignItems={"center"}
              display={"flex"}
            >
              <Icon fontSize="inherit" sx={{ color: alpha(iconColor, 0.9) }} />
            </Box>
          </Grid>
          <Grid item xs>
            <Grid
              container
              spacing={0}
              paddingLeft={2}
              flexDirection={"column"}
            >
              <Grid item>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: "normal",
                  }}
                >
                  {titleMain}
                </Typography>
              </Grid>
              <Grid item>
                <Grid container flexDirection={"row"} alignItems={"center"} spacing={1}>
                  <Grid item alignItems={"center"} alignContent={"center"} marginBottom={1} marginRight={2}>
                    <Typography
                      variant="h4"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {AnimatedNumber(contentMain)}
                    </Typography>
                  </Grid>
                  {contentSecondary > 0 && (
                    <>
                      <Grid
                        item
                        display={"flex"}
                        justifyContent={"flex-start"}
                      >
                        <Box
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primaryChart,
                              0.1
                            ),
                            borderRadius: "5px",
                            paddingLeft:2,
                            paddingRight:2,
                            height: 30,
                          }}
                          alignItems={"center"}
                          alignContent={"center"}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              color: alpha(theme.palette.primaryChart, 0.9),
                              fontWeight: "bold",
                            }}
                          >
                            +{contentSecondary}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item >
                        <Typography variant="body1">
                          {titleSecondary}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
